// Copyright text placeholder, Warner Bros. Discovery, Inc.

/**
 *  WARNING:
 *  This file is auto-generated. Do not edit manually. Any changes made to this
 *  file will be overwritten the next time it is generated. If you need to make
 *  changes, please modify the source files and re-generate this file.
 */

import { BaseError, type IVersionedEvent } from '../../../core';

/**
 *
 *
 * version 1.0
 * @public
 */
interface IErrV1 {
  /** A classifier for the triggered error. Should default to the native instance identifier of the error. This field will be used for aggregation. **/
  classification?: string;
  /** Unique identifier generated for each error created. **/
  id: string;
  /** When set, signifies that this event represents an error, and indicates the high-level category and scope of the error.
     USER_FACING errors represent any situation where the user was directly impacted by the error. It may manifest as an error popup, application crash, missing content or any action critical to the user journey.
     RECOVERABLE errors represent any error that degrades user experience. A subset of the action was performed but failed. The user can still continue on their journey.
     BACKGROUND errors represent any error that occurs but there is no impact to the user. The error is important to capture and watch for anomalies but does not raise immediate operational concerns. **/
  scope?: string;
  /** Human readable description of the error. Used in debugging to summarize the error, but will not be used in aggregation.
     User/device/entity specific dynamic values should NOT be used in this message to allow for optimal grouping during debugging. **/
  message?: string;
  /** A string output of methods that were called when the Error was thrown. This trace is typically provided by the native error handler and includes file and line numbers. **/
  stackTrace?: string;
}

/**
 *
 *
 * version 1.0
 * @public
 */
interface IOmdV1 {
  /** Uniquely identifiable name for each sdk and application. One Jira team may be responsible for multiple components. Other OMD information can be inferred from this value. **/
  component?: string;
}

/**
 *
 *
 * version 1.0
 * @public
 */
interface ICrashV1Payload {
  /**  **/
  err: IErrV1;
  /**  **/
  omd?: IOmdV1;
}

/**
 * properties for PlatformCrashV1 error
 * @public
 */
export interface IPlatformCrashV1Props extends Omit<ICrashV1Payload, 'err'> {
  /** error object **/
  err: Omit<ICrashV1Payload['err'], 'id'>;
}

/**
 * ICrashV1 error
 * An event emitted during/after an application has stopped due to a fatal exception. This event is used for capturing unexpected terminated sessions and empowers Error Free Session Rate.
 * @public
 */
export class PlatformCrashV1 extends BaseError<IPlatformCrashV1Props, ICrashV1Payload> {
  /** unique event type **/
  public readonly eventType: string = 'beam.errors.platform.crash_1.0';

  /**
   * overrides the default _build method to add default values for fields
   * @param props - IErrV1
   * @returns IErrV1
   */
  private _buildErrV1(props: IErrV1): IErrV1 {
    return {
      ...props,
      classification: props.classification ?? 'Error',
      id: this._getErrorId(),
      scope: props.scope ?? 'USER_FACING',
      stackTrace: props.stackTrace?.slice(0, 3000)
    };
  }
  /**
   * overrides the default _build method to add default values for fields
   * @param props - IOmdV1
   * @returns IOmdV1
   */
  private _buildOmdV1(props: IOmdV1): IOmdV1 {
    return props;
  }

  /**
   * @param payload - The payload for the error
   * @returns IVersionedEvent<IPlatformCrashV1Props>
   */
  protected override _build(props: IPlatformCrashV1Props): IVersionedEvent<ICrashV1Payload> {
    const payload: ICrashV1Payload = {
      ...props,
      err: this._buildErrV1(props.err as IErrV1),
      omd: this._buildOmdV1(props.omd as IOmdV1)
    };

    return super._build(payload);
  }
}

export type { IErrV1 as IPlatformCrashV1Err };
export type { IOmdV1 as IPlatformCrashV1Omd };
export type { ICrashV1Payload as IPlatformCrashV1Payload };
